import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Search, Plus, Edit, Trash2, User, Database, Users, RefreshCw, Loader2, ExternalLink, CheckCircle, Truck, XCircle, Upload, Download, Building2, Link2, Unlink, AlertTriangle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CSVImportModal from '../../components/CSVImportModal';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PERMS } from '../../utils/permissions';
import TablePagination from '../../components/TablePagination';
import GenericBulkActionsBar from '../../components/GenericBulkActionsBar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ExportFormatMenu from '../../components/common/ExportFormatMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExpirationBadge = ({ dateStr }) => {
  if (!dateStr) return <span className="text-xs text-gray-400">N/A</span>;
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  const formatted = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  if (diffDays < 0) return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full" data-testid="expiration-expired">
      <XCircle className="w-3 h-3" /> {formatted}
    </span>
  );
  if (diffDays <= 15) return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="expiration-warning">
      <AlertTriangle className="w-3 h-3" /> {formatted}
    </span>
  );
  if (diffDays <= 30) return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full" data-testid="expiration-soon">
      <Clock className="w-3 h-3" /> {formatted}
    </span>
  );
  return <span className="text-xs text-gray-600">{formatted}</span>;
};

const DriversManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  // RBAC V2 Phase 6 — permissions granulaires
  // Flotte & tiers (juin 2026) : commerciaux ET opérateurs (tout type) ont l'accès
  // complet — lecture + écriture + suppression.
  const isFleetWriter = ['commercial', 'lead_commercial', 'field_ops', 'lead_field_ops', 'ops', 'lead_ops', 'operateur'].includes(user?.role);
  const canCreate = isAdmin || hasPermission(PERMS.DRIVERS_CREATE) || isFleetWriter;
  const canEdit = isAdmin || hasPermission(PERMS.DRIVERS_EDIT) || isFleetWriter;
  const canDelete = isAdmin || hasPermission(PERMS.DRIVERS_DEACTIVATE) || isFleetWriter;
  const canAssign = isAdmin || hasPermission(PERMS.DRIVERS_ASSIGN) || isFleetWriter;
  
  // Source tab: 'local', 'external', 'all'
  const [sourceTab, setSourceTab] = useState('local');
  
  // Local drivers state
  const [localDrivers, setLocalDrivers] = useState([]);
  const [filteredLocalDrivers, setFilteredLocalDrivers] = useState([]);
  
  // External drivers state (from Truck Database)
  const [externalDrivers, setExternalDrivers] = useState([]);
  const [filteredExternalDrivers, setFilteredExternalDrivers] = useState([]);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalStats, setExternalStats] = useState({ total: 0, lastSync: null });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [showCSVImport, setShowCSVImport] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Bulk selection state
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Partner assignment state
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [partnerDialogDriver, setPartnerDialogDriver] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);

  const openPartnerDialog = (driver) => {
    setPartnerDialogDriver(driver);
    setPartnerSearch('');
    setPartnerResults([]);
    setPartnerDialogOpen(true);
    searchPartners('');
  };

  const searchPartners = async (q) => {
    setPartnerLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (q && q.length >= 2) params.append('search', q);
      const res = await axios.get(`${API}/transport-partners/search-for-assignment?${params}`, {});
      setPartnerResults(res.data.partners || []);
    } catch { setPartnerResults([]); }
    finally { setPartnerLoading(false); }
  };

  const handleAssignPartner = async (partnerId, partnerName) => {
    if (!partnerDialogDriver) return;
    try {
      await axios.post(`${API}/drivers/${partnerDialogDriver.id}/assign-partner`, {
        partnerId, partnerName
      }, {});
      toast({ title: `Chauffeur rattache a ${partnerName}` });
      setPartnerDialogOpen(false);
      setPartnerDialogDriver(null);
      fetchLocalDrivers();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const handleUnassignPartner = async (driverId) => {
    try {
      await axios.post(`${API}/drivers/${driverId}/unassign-partner`, {}, {});
      toast({ title: 'Chauffeur detache du transporteur' });
      fetchLocalDrivers();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  // Fetch local drivers
  const fetchLocalDrivers = useCallback(async () => {
    try {
      const include_inactive = statusFilter === 'all' || statusFilter === 'inactive';
      const response = await axios.get(`${API}/drivers?include_inactive=${include_inactive}`);
      let data = response.data;
      
      if (statusFilter === 'inactive') {
        data = data.filter(d => d.status === 'inactif');
      }
      
      // Mark all as local source
      data = data.map(d => ({ ...d, source: 'local' }));
      
      setLocalDrivers(data);
      setFilteredLocalDrivers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching local drivers:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les chauffeurs locaux",
        variant: "destructive"
      });
      setLoading(false);
    }
  }, [statusFilter]);

  // Fetch external drivers from Truck Database
  const fetchExternalDrivers = useCallback(async (forceRefresh = false) => {
    setExternalLoading(true);
    try {
      const response = await axios.get(`${API}/external/drivers`, {
        params: { limit: 500, force_refresh: forceRefresh },
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      const drivers = (response.data.drivers || []).map(d => ({
        ...d,
        source: 'external',
        // Map external fields to local field names for display
        firstName: d.nom?.split(' ')[0] || d.nom || '',
        lastName: d.nom?.split(' ').slice(1).join(' ') || '',
        phone: d.telephone || '',
        status: d.disponible ? 'disponible' : 'occupe',
        permitNumber: d.numero_permis || '',
        permitType: d.categorie_permis || '',
        permitExpirationDate: d.date_expiration_permis
      }));
      
      setExternalDrivers(drivers);
      setFilteredExternalDrivers(drivers);
      setExternalStats({
        total: response.data.count || drivers.length,
        totalCached: response.data.total_cached || drivers.length,
        lastSync: response.data.last_sync
      });
    } catch (error) {
      console.error('Error fetching external drivers:', error);
      // Don't show error toast for external drivers - it's optional
    } finally {
      setExternalLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocalDrivers();
    fetchExternalDrivers();
  }, [fetchLocalDrivers, fetchExternalDrivers]);

  // Filter drivers based on search and source
  useEffect(() => {
    const filterDrivers = (drivers) => {
      if (!searchQuery) return drivers;
      return drivers.filter(driver => {
        const name = `${driver.firstName || ''} ${driver.lastName || ''} ${driver.nom || ''}`.toLowerCase();
        const phone = driver.phone || driver.telephone || '';
        const permit = driver.permitNumber || driver.numero_permis || '';
        const matricule = driver.matricule || '';
        
        return name.includes(searchQuery.toLowerCase()) ||
          phone.includes(searchQuery) ||
          permit.toLowerCase().includes(searchQuery.toLowerCase()) ||
          matricule.toLowerCase().includes(searchQuery.toLowerCase());
      });
    };
    
    setFilteredLocalDrivers(filterDrivers(localDrivers));
    setFilteredExternalDrivers(filterDrivers(externalDrivers));
    setCurrentPage(1);
  }, [searchQuery, localDrivers, externalDrivers]);

  // Get current displayed drivers based on source tab
  const getCurrentDrivers = () => {
    switch (sourceTab) {
      case 'local':
        return filteredLocalDrivers;
      case 'external':
        return filteredExternalDrivers;
      case 'all':
        return [...filteredLocalDrivers, ...filteredExternalDrivers];
      default:
        return filteredLocalDrivers;
    }
  };

  const currentDrivers = getCurrentDrivers();

  const handleDeleteDriver = async (driverId, driverName, source) => {
    if (source === 'external') {
      toast({
        title: "Action non disponible",
        description: "Les chauffeurs externes ne peuvent pas être supprimés depuis ce portail",
        variant: "destructive"
      });
      return;
    }
    
    const action = user?.role === 'admin' ? 'supprimer' : 'désactiver';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} ${driverName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/drivers/${driverId}`);
      toast({
        title: "✅ Chauffeur " + (user?.role === 'admin' ? 'supprimé' : 'désactivé'),
        description: `${driverName} a été ${user?.role === 'admin' ? 'supprimé' : 'désactivé'} avec succès`
      });
      fetchLocalDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de supprimer le chauffeur",
        variant: "destructive"
      });
    }
  };

  // Bulk selection handlers
  const handleSelectDriver = (driverId) => {
    setSelectedDrivers(prev => 
      prev.includes(driverId) 
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleSelectAll = () => {
    const currentPageDrivers = currentDrivers
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .filter(d => d.source === 'local'); // Only allow selecting local drivers
    
    if (selectAll) {
      setSelectedDrivers(prev => prev.filter(id => !currentPageDrivers.find(d => d.id === id)));
    } else {
      const newIds = currentPageDrivers.map(d => d.id);
      setSelectedDrivers(prev => [...new Set([...prev, ...newIds])]);
    }
    setSelectAll(!selectAll);
  };

  const handleBulkDelete = async () => {
    const action = user?.role === 'admin' ? 'supprimer' : 'désactiver';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} ${selectedDrivers.length} chauffeur(s) ?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bulk-actions/drivers/delete`, {
        driverIds: selectedDrivers
      });
      toast({
        title: "✅ Opération réussie",
        description: response.data.message
      });
      setSelectedDrivers([]);
      setSelectAll(false);
      fetchLocalDrivers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'opération bulk",
        variant: "destructive"
      });
    }
  };

  const handleBulkActivate = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/drivers/activate`, {
        driverIds: selectedDrivers
      });
      toast({
        title: "✅ Chauffeurs activés",
        description: response.data.message
      });
      setSelectedDrivers([]);
      setSelectAll(false);
      fetchLocalDrivers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'activation",
        variant: "destructive"
      });
    }
  };

  const handleBulkExport = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/drivers/export`, {
        driverIds: selectedDrivers
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chauffeurs_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "✅ Export réussi",
        description: `${selectedDrivers.length} chauffeur(s) exporté(s)`
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de l'export",
        variant: "destructive"
      });
    }
  };

  const handleSyncExternalDrivers = async () => {
    toast({ title: "Synchronisation en cours...", description: "Récupération des chauffeurs depuis Truck Database" });
    await fetchExternalDrivers(true);
    toast({ title: "✅ Synchronisation terminée", description: `${externalStats.total} chauffeurs récupérés` });
  };

  // Reset selectAll when page changes
  useEffect(() => {
    const currentPageDrivers = currentDrivers
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .filter(d => d.source === 'local');
    const allSelected = currentPageDrivers.length > 0 && 
      currentPageDrivers.every(d => selectedDrivers.includes(d.id));
    setSelectAll(allSelected);
  }, [currentPage, currentDrivers, selectedDrivers]);

  const getStatusBadge = (driver) => {
    const status = driver.status || (driver.disponible ? 'disponible' : 'occupe');
    const configs = {
      disponible: { dotColor: 'bg-green-500', label: 'Disponible' },
      occupe: { dotColor: 'bg-amber-500', label: 'Occupé' },
      inactif: { dotColor: 'bg-gray-400', label: 'Inactif' }
    };
    const config = configs[status] || configs.disponible;
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
        <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
        {config.label}
      </span>
    );
  };

  const getSourceBadge = (source) => {
    if (source === 'local') {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <Users className="w-3 h-3" />
          Local
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Database className="w-3 h-3" />
        Truck DB
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDriverName = (driver) => {
    if (driver.firstName && driver.lastName) {
      return `${driver.firstName} ${driver.lastName}`;
    }
    return driver.nom || 'N/A';
  };

  const getDriverInitials = (driver) => {
    const name = getDriverName(driver);
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const localStats = {
    total: localDrivers.length,
    disponible: localDrivers.filter(d => d.status === 'disponible').length,
    occupe: localDrivers.filter(d => d.status === 'occupe').length,
    inactif: localDrivers.filter(d => d.status === 'inactif').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Gestion des Chauffeurs</h1>
          <p className="text-gray-500 mt-1">Gérer tous les chauffeurs du parc automobile</p>
        </div>
        <div className="flex gap-2">
          {sourceTab === 'external' && (
            <Button
              variant="outline"
              onClick={handleSyncExternalDrivers}
              disabled={externalLoading}
            >
              {externalLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Synchroniser
            </Button>
          )}
          {(isAdmin || isFleetWriter) && sourceTab !== 'external' && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowCSVImport(true)}
                data-testid="import-drivers-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importer CSV
              </Button>
              <ExportFormatMenu
                endpoint={`${API}/export/drivers`}
                filenameBase="BaobabConnect_Chauffeurs"
                testId="export-drivers"
                onToast={toast}
              />
              <Button
                onClick={() => navigate('/admin/drivers/new')}
                className="bg-[#1db584] hover:bg-[#16a075]"
                disabled={!canCreate}
                title={!canCreate ? 'Permission requise : drivers.create' : ''}
                data-testid="new-driver-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau chauffeur
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Source Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => { setSourceTab('local'); setCurrentPage(1); }}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            sourceTab === 'local'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Users className="w-4 h-4" />
          Locaux
          <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{localDrivers.length}</span>
        </button>
        <button
          onClick={() => { setSourceTab('external'); setCurrentPage(1); }}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            sourceTab === 'external'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Database className="w-4 h-4" />
          Externes (Truck DB)
          <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {externalLoading ? '...' : externalDrivers.length}
          </span>
        </button>
        <button
          onClick={() => { setSourceTab('all'); setCurrentPage(1); }}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            sourceTab === 'all'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          Tous
          <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {localDrivers.length + externalDrivers.length}
          </span>
        </button>
      </div>

      {/* Stats Cards - Only show for local drivers */}
      {sourceTab === 'local' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Local</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900">{localStats.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 flex items-center justify-center gap-1"><CheckCircle className="w-4 h-4" /> Disponibles</p>
                <p className="text-xl md:text-3xl font-bold text-gray-700">{localStats.disponible}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 flex items-center justify-center gap-1"><Truck className="w-4 h-4" /> Occupés</p>
                <p className="text-xl md:text-3xl font-bold text-gray-700">{localStats.occupe}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 flex items-center justify-center gap-1"><XCircle className="w-4 h-4" /> Inactifs</p>
                <p className="text-xl md:text-3xl font-bold text-gray-500">{localStats.inactif}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* External Stats */}
      {sourceTab === 'external' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Chauffeurs Truck Database</p>
                <p className="text-sm text-blue-700">
                  {externalStats.totalCached || externalDrivers.length} chauffeurs en cache
                  {externalStats.lastSync && (
                    <span className="ml-2 text-blue-500">
                      • Dernière sync: {new Date(externalStats.lastSync).toLocaleString('fr-FR')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {externalLoading && <Loader2 className="w-5 h-5 animate-spin text-blue-600" />}
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, téléphone, permis, matricule..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {sourceTab === 'local' && (
              <div className="flex gap-2">
                <Button
                  variant={statusFilter === 'active' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('active')}
                >
                  Actifs
                </Button>
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('all')}
                >
                  Tous
                </Button>
                <Button
                  variant={statusFilter === 'inactive' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('inactive')}
                >
                  Inactifs
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Drivers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Liste des Chauffeurs ({currentDrivers.length})
            {sourceTab === 'all' && (
              <span className="text-sm font-normal text-gray-500">
                ({filteredLocalDrivers.length} locaux + {filteredExternalDrivers.length} externes)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {sourceTab !== 'external' && (
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                )}
                <TableHead>Chauffeur</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Permis</TableHead>
                <TableHead>Expiration</TableHead>
                <TableHead>Transporteur</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={sourceTab !== 'external' ? 9 : 8} className="text-center py-8 text-gray-500">
                    {externalLoading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Chargement des chauffeurs...</span>
                      </div>
                    ) : (
                      'Aucun chauffeur trouvé'
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                currentDrivers
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((driver) => (
                  <TableRow 
                    key={`${driver.source}-${driver.id}`} 
                    className={`hover:bg-gray-50 ${selectedDrivers.includes(driver.id) && driver.source === 'local' ? 'bg-blue-50' : ''}`}
                  >
                    {sourceTab !== 'external' && (
                      <TableCell>
                        {driver.source === 'local' ? (
                          <Checkbox 
                            checked={selectedDrivers.includes(driver.id)}
                            onCheckedChange={() => handleSelectDriver(driver.id)}
                          />
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          driver.source === 'local' ? 'bg-[#1db584]' : 'bg-blue-500'
                        }`}>
                          {getDriverInitials(driver)}
                        </div>
                        <div>
                          <p className="font-medium">{getDriverName(driver)}</p>
                          {driver.matricule && (
                            <p className="text-xs text-gray-500">{driver.matricule}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getSourceBadge(driver.source)}
                    </TableCell>
                    <TableCell>{driver.phone || driver.telephone || 'N/A'}</TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {driver.permitCategories && Array.isArray(driver.permitCategories) && driver.permitCategories.length > 0 ? (
                            driver.permitCategories.map((cat, i) => {
                              const isExpired = cat.expiresAt && cat.expiresAt !== 'PERMANENT' && cat.expiresAt !== 'permanent' && new Date(cat.expiresAt) < new Date();
                              const isCritical = ['C', 'D', 'E', 'CE', 'EC'].includes(cat.category);
                              return (
                                <span key={i} className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                  isExpired ? 'bg-red-100 text-red-700 border border-red-200 line-through' :
                                  isCritical ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                                  'bg-gray-100 text-gray-700 border border-gray-200'
                                }`} title={`${cat.category}: ${cat.expiresAt || 'N/A'}`} data-testid={`driver-cat-badge-${cat.category}`}>
                                  {cat.category}
                                  {isExpired && <XCircle className="w-2.5 h-2.5 ml-0.5" />}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-xs font-medium text-gray-600">{driver.permitType || driver.categorie_permis || 'N/A'}</span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5">{driver.permitNumber || driver.numero_permis || ''}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ExpirationBadge dateStr={driver.permitExpirationDate || driver.date_expiration_permis} />
                    </TableCell>
                    <TableCell data-testid={`driver-partner-${driver.id}`}>
                      {driver.partnerName ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full max-w-[140px] truncate" title={driver.partnerName}>
                          <Building2 className="w-3 h-3 shrink-0" />
                          {driver.partnerName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(driver)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {driver.source === 'local' ? (
                          <>
                            {(isAdmin || isFleetWriter) && (
                              driver.partnerId ? (
                                <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
                                  onClick={() => handleUnassignPartner(driver.id)}
                                  title={`Detacher de ${driver.partnerName}`}
                                  data-testid={`unassign-partner-driver-${driver.id}`}>
                                  <Unlink className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
                                  onClick={() => openPartnerDialog(driver)}
                                  title={canAssign ? "Rattacher a un transporteur" : "Permission requise : drivers.assign"}
                                  disabled={!canAssign}
                                  data-testid={`assign-partner-driver-${driver.id}`}>
                                  <Link2 className="w-4 h-4" />
                                </Button>
                              )
                            )}
                            <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                              onClick={() => navigate(`/admin/drivers/${driver.id}/edit`)}
                              title="Voir details">
                              <User className="w-4 h-4" />
                            </Button>
                            {(canEdit || canDelete) && (
                              <>
                                <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                                  onClick={() => navigate(`/admin/drivers/${driver.id}/edit`)}
                                  title={canEdit ? "Modifier" : "Permission requise : drivers.edit"}
                                  disabled={!canEdit}
                                  data-testid={`edit-driver-${driver.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                  onClick={() => handleDeleteDriver(driver.id, getDriverName(driver), driver.source)}
                                  title={canDelete ? (isAdmin ? 'Supprimer' : 'Desactiver') : "Permission requise : drivers.deactivate"}
                                  disabled={driver.currentOperationId || !canDelete}
                                  data-testid={`delete-driver-${driver.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </>
                        ) : (
                          <Button size="sm" variant="outline" className="text-blue-600" title="Voir dans Truck Database">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          <TablePagination
            currentPage={currentPage}
            totalItems={currentDrivers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            itemLabel="chauffeurs"
          />
        </CardContent>
      </Card>
      
      {/* Bulk Actions Bar - Only for local drivers */}
      {sourceTab !== 'external' && selectedDrivers.length > 0 && (
        <GenericBulkActionsBar
          entityType="drivers"
          selectedCount={selectedDrivers.length}
          onClearSelection={() => { setSelectedDrivers([]); setSelectAll(false); }}
          onDelete={handleBulkDelete}
          onActivate={handleBulkActivate}
          onExport={handleBulkExport}
        />
      )}

      <CSVImportModal
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        type="drivers"
        columns={[
          { key: 'firstName', label: 'Prénom' },
          { key: 'lastName', label: 'Nom' },
          { key: 'phone', label: 'Téléphone' },
          { key: 'licenseType', label: 'Permis' },
          { key: 'experience', label: 'Exp.' },
        ]}
        onSuccess={() => fetchLocalDrivers()}
      />

      {/* Partner Assignment Dialog */}
      <Dialog open={partnerDialogOpen} onOpenChange={setPartnerDialogOpen}>
        <DialogContent className="max-w-md" data-testid="driver-partner-assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Rattacher {partnerDialogDriver ? getDriverName(partnerDialogDriver) : ''} a un transporteur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Rechercher un transporteur..."
              value={partnerSearch}
              onChange={(e) => { setPartnerSearch(e.target.value); searchPartners(e.target.value); }}
              className="h-9"
              data-testid="driver-partner-search-input"
              autoFocus
            />
            <div className="max-h-[360px] overflow-y-auto space-y-1">
              {partnerLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : partnerResults.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Aucun transporteur trouve</p>
              ) : (
                partnerResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleAssignPartner(p.id, p.name)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-transparent hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                    data-testid={`driver-partner-option-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0">
                      {(p.name || '?').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                      <p className="text-xs text-gray-400">{p.type === 'Independant' ? 'Independant' : (p.type || '')} {p.phone ? `· ${p.phone}` : ''}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversManagement;
